import { FIELD_LABELS, getPlanType, BP_TECHNIQUE, GLU_TECHNIQUE, WT_TECHNIQUE } from './rpmCarePlanConfig';
import { EVITALS_LOGO_BASE64 } from '../../../../assets/evitalsLogoBase64';

export function hexToRgba(hex, alpha) {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.replace(/(.)/g, '$1$1') : h;
  const n = Number.parseInt(full, 16);
  if (!Number.isFinite(n)) return `rgba(27,42,74,${alpha})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

export function formatPlanDate(iso) {
  if (!iso) return '—';
  const raw = String(iso);
  const d = raw.length <= 10 ? new Date(`${raw}T00:00:00`) : new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
}

export function formatPlanDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  let h = d.getHours();
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ap = h >= 12 ? 'PM' : 'AM';
  h %= 12;
  if (h === 0) h = 12;
  return `${mm}/${dd}/${d.getFullYear()}, ${h}:${mi} ${ap}`;
}

export function scalePosition(value, min, max) {
  if (!Number.isFinite(value) || max === min) return 0;
  return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
}

export function pickedItems(list) {
  return (list || [])
    .filter((item) => item.checked)
    .map((item) => ({ label: item.label, custom: Boolean(item.custom) }));
}

export function resolveSelectValue(isCustom, customValue, presetValue) {
  if (isCustom) {
    const v = String(customValue || '').trim();
    return v || 'Custom (unspecified)';
  }
  return presetValue;
}

export function applySelectValue(value, options) {
  if (value == null) {
    return { isCustom: false, custom: '', preset: options[0] || '' };
  }
  if (options.includes(value)) {
    return { isCustom: false, custom: '', preset: value };
  }
  return { isCustom: true, custom: value, preset: options[0] || '' };
}

export function getLatestVitalsSummary({ bp, bg, bgDisplayValue, weight, bpStatus, pulseStatus }) {
  const latest = {
    systolic: bp?.systolic_pressure != null ? Number(bp.systolic_pressure) : null,
    diastolic: bp?.diastolic_pressure != null ? Number(bp.diastolic_pressure) : null,
    pulse: bp?.pulse != null ? Number(bp.pulse) : null,
    glucose: Number.isFinite(Number(bgDisplayValue)) ? Number(bgDisplayValue) : null,
    weight: weight?.weight != null ? Number(weight.weight) : null,
    bpOutOfRange: Boolean(bpStatus?.isOutOfRange),
    pulseLow: pulseStatus === 'low',
  };
  return latest;
}

export function buildPrefillHtml(typeKey, latest) {
  if (typeKey === 'bp') {
    if (latest.systolic != null && latest.diastolic != null) {
      const pulseBit = latest.pulse != null
        ? ` · pulse <b>${latest.pulse} bpm${latest.pulseLow ? ' (low)' : ''}</b>`
        : '';
      const rangeBit = latest.bpOutOfRange ? ' · BP target out of range' : '';
      return `Prefilled from the record. Latest reading <b>${latest.systolic}/${latest.diastolic} mmHg</b>${pulseBit}${rangeBit}.`;
    }
    return 'No recent blood pressure reading on file. Confirm the goal with the provider at setup.';
  }
  if (typeKey === 'glucose') {
    if (latest.glucose != null) {
      return `Prefilled from the record. Latest glucose <b>${latest.glucose} mg/dL</b>.`;
    }
    return 'No recent glucose reading on file. Confirm fasting targets with the provider at setup.';
  }
  if (typeKey === 'weight') {
    if (latest.weight != null) {
      return `Prefilled from the record. Latest weight <b>${latest.weight} lb</b>.`;
    }
    return 'No baseline weight on file. Confirm the patient’s scale and starting weight at setup.';
  }
  return '';
}

export function readGoal(typeKey, form) {
  if (typeKey === 'bp') {
    return {
      systolic: Number(form.systolic) || 0,
      diastolic: Number(form.diastolic) || 0,
      basis: form.basis,
      reviewDate: form.reviewDate,
    };
  }
  if (typeKey === 'glucose') {
    return {
      fastingLow: Number(form.fastingLow) || 0,
      fastingHigh: Number(form.fastingHigh) || 0,
      postMeal: Number(form.postMeal) || 0,
      a1c: Number(form.a1c) || 0,
      basis: form.basis,
      reviewDate: form.reviewDate,
    };
  }
  if (typeKey === 'weight') {
    return {
      targetWeight: Number(form.targetWeight) || 0,
      basis: form.basis,
      dayGain: Number(form.dayGain) || 0,
      weekGain: Number(form.weekGain) || 0,
      reviewDate: form.reviewDate,
    };
  }
  return {
    title: form.title,
    focus: form.focus,
    metric: form.metric,
    target: form.target,
    description: form.description,
    reviewDate: form.reviewDate,
  };
}

export function readMonitoring(typeKey, form) {
  const shared = {
    device: form.device,
    schedule: resolveSelectValue(form.scheduleIsCustom, form.scheduleCustom, form.schedule),
    requiredDaysPer30: Number(form.requiredDaysPer30) || 0,
    missedReadingAlert: resolveSelectValue(form.gapIsCustom, form.gapCustom, form.missedReadingAlert),
  };
  if (typeKey === 'custom') {
    return { ...shared, escalation: form.escalation || '' };
  }
  return shared;
}

export function collectPlanPayload(form, { patientId, sessionUser, existingSignoff } = {}) {
  const type = getPlanType(form.typeKey);
  const approvedPending = form.approvedBy === '__pending__';
  const planTitle = form.typeKey === 'custom'
    ? (String(form.title || '').trim() || 'Custom Care Plan')
    : type.planTitle;
  return {
    resourceType: 'CarePlan',
    typeKey: form.typeKey,
    planType: type.label,
    planTitle,
    patientRef: patientId,
    condition: type.condition,
    status: 'active',
    goal: readGoal(form.typeKey, form),
    monitoring: readMonitoring(form.typeKey, form),
    interventions: pickedItems(form.interventions),
    education: pickedItems(form.education),
    notes: form.notes || '',
    signoff: {
      createdBy: existingSignoff?.createdBy || sessionUser?.username || sessionUser?.name || 'unknown',
      approvedBy: approvedPending ? null : form.approvedBy,
      approvalStatus: approvedPending ? 'pending' : 'approved',
      createdDate: existingSignoff?.createdDate || new Date().toISOString(),
    },
  };
}

export function formFromPlan(plan) {
  const type = getPlanType(plan.typeKey);
  const goal = plan.goal || {};
  const monitoring = plan.monitoring || {};
  const scheduleSel = applySelectValue(monitoring.schedule, type.scheduleOptions);
  const gapSel = applySelectValue(monitoring.missedReadingAlert, type.gapOptions);

  const markChecked = (configItems, saved) => {
    const savedList = saved || [];
    const base = (configItems || []).map(([label, desc]) => {
      const hit = savedList.find((s) => s.label === label && !s.custom);
      return { label, desc: desc || '', checked: Boolean(hit), custom: false };
    });
    savedList.forEach((item) => {
      if (item.custom || !base.some((b) => b.label === item.label)) {
        base.push({ label: item.label, desc: '', checked: true, custom: true });
      }
    });
    return base;
  };

  return {
    typeKey: plan.typeKey,
    reviewDate: goal.reviewDate || '',
    systolic: goal.systolic ?? type.defaults.systolic,
    diastolic: goal.diastolic ?? type.defaults.diastolic,
    fastingLow: goal.fastingLow ?? type.defaults.fastingLow,
    fastingHigh: goal.fastingHigh ?? type.defaults.fastingHigh,
    postMeal: goal.postMeal ?? type.defaults.postMeal,
    a1c: goal.a1c ?? type.defaults.a1c,
    targetWeight: goal.targetWeight ?? type.defaults.targetWeight,
    dayGain: goal.dayGain ?? type.defaults.dayGain,
    weekGain: goal.weekGain ?? type.defaults.weekGain,
    title: goal.title ?? '',
    focus: goal.focus ?? '',
    metric: goal.metric ?? '',
    target: goal.target ?? '',
    description: goal.description ?? '',
    basis: goal.basis ?? type.defaults.basis,
    device: monitoring.device ?? type.defaults.device ?? '',
    schedule: scheduleSel.preset,
    scheduleIsCustom: scheduleSel.isCustom,
    scheduleCustom: scheduleSel.custom,
    requiredDaysPer30: monitoring.requiredDaysPer30 ?? type.defaults.requiredDaysPer30,
    missedReadingAlert: gapSel.preset,
    gapIsCustom: gapSel.isCustom,
    gapCustom: gapSel.custom,
    escalation: monitoring.escalation ?? '',
    interventions: markChecked(type.interventions, plan.interventions),
    education: markChecked(type.education, plan.education),
    notes: plan.notes || '',
    approvedBy: plan.signoff?.approvalStatus === 'pending'
      ? '__pending__'
      : (plan.signoff?.approvedBy || '__pending__'),
  };
}

export function getEscalationBands(typeKey, goal) {
  const g = goal || {};
  if (typeKey === 'bp') {
    return [
      ['#1f9d55', 'At or below goal', `Up to ${g.systolic}/${g.diastolic} mmHg`, 'Routine trending. Reinforce adherence at the monthly contact.'],
      ['#b45309', 'Elevated', '140–159 systolic or 90–99 diastolic', 'Call within one business day. Review technique, adherence, sodium, recent changes.'],
      ['#c2560f', 'High', '160–179 systolic or 100–119 diastolic', 'Contact the same day. Repeat after five minutes rest. Notify provider within 24 hours.'],
      ['#d92d20', 'Crisis', '180+ systolic, or 120+ diastolic', 'Re-check after five minutes. If still elevated, contact provider immediately; call 911 with symptoms.'],
      ['#6b7686', 'Low or symptomatic', 'Below 90/60, or pulse below 50', 'Assess symptoms. Change nothing without provider instruction; notify same day.'],
    ];
  }
  if (typeKey === 'glucose') {
    return [
      ['#d92d20', 'Low', 'Below 70 mg/dL', 'Treat with 15 g fast-acting carbs, re-check in 15 minutes. Below 54, or unable to treat — call 911.'],
      ['#1f9d55', 'In range', `70–${g.fastingHigh} fasting; below ${g.postMeal} after meals`, 'Routine trending. Reinforce diet, medication, and activity.'],
      ['#b45309', 'Elevated', `Fasting ${(g.fastingHigh || 0) + 1}–180`, 'Review carbs, medication timing, and adherence at the next contact.'],
      ['#c2560f', 'High', '181–250 mg/dL', 'Contact the same day. Check for missed medication and hydration.'],
      ['#d92d20', 'Very high', 'Above 250, or with nausea/vomiting', 'Notify provider immediately. Check ketones if able; call 911 for vomiting, confusion, or trouble breathing.'],
    ];
  }
  if (typeKey === 'weight') {
    return [
      ['#1f9d55', 'Stable', `Within ${Math.max(0, (g.dayGain || 1) - 1)} lb of recent weight`, 'Routine trending. Reinforce daily weigh-ins.'],
      ['#b45309', 'Gaining', `Up ${g.dayGain} lb in a day or ${g.weekGain} lb in a week`, 'Contact the care team. Review sodium, fluids, and diuretic adherence.'],
      ['#d92d20', 'Rapid gain', `Over ${g.dayGain} lb overnight, or over ${g.weekGain} lb in a week`, 'Notify provider the same day for possible medication change.'],
      ['#6b7686', 'With symptoms', 'New swelling, or shortness of breath', 'Contact provider promptly; call 911 for severe breathing difficulty.'],
    ];
  }
  return [];
}

export function getSummaryRows(plan) {
  const type = getPlanType(plan.typeKey);
  const g = plan.goal || {};
  const m = plan.monitoring || {};
  if (plan.typeKey === 'bp') {
    return [
      ['BP goal', `${g.systolic}/${g.diastolic} mmHg`],
      ['Schedule', m.schedule],
      ['Missed-reading alert', m.missedReadingAlert],
      ['Next review', formatPlanDate(g.reviewDate)],
    ];
  }
  if (plan.typeKey === 'glucose') {
    return [
      ['Fasting goal', `${g.fastingLow}–${g.fastingHigh} mg/dL`],
      ['A1c goal', `< ${g.a1c}%`],
      ['Schedule', m.schedule],
      ['Next review', formatPlanDate(g.reviewDate)],
    ];
  }
  if (plan.typeKey === 'weight') {
    return [
      ['Target weight', `${g.targetWeight} lb`],
      ['Focus', g.basis],
      ['Daily gain alert', `+${g.dayGain} lb`],
      ['Weekly gain alert', `+${g.weekGain} lb`],
      ['Schedule', m.schedule],
      ['Next review', formatPlanDate(g.reviewDate)],
    ];
  }
  return [
    ['Focus', g.focus || '—'],
    ['Target', `${g.metric ? `${g.metric} ` : ''}${g.target || '—'}`],
    ['Schedule', m.schedule],
    ['Next review', formatPlanDate(g.reviewDate)],
  ];
}

export function getPrintGoal(plan) {
  const g = plan.goal || {};
  if (plan.typeKey === 'bp') {
    return {
      big: `${g.systolic}/${g.diastolic}`,
      label: 'Your blood pressure goal',
      sub: `Keep your readings at or below this number. ${g.basis || ''}.`,
    };
  }
  if (plan.typeKey === 'glucose') {
    return {
      big: `${g.fastingLow}–${g.fastingHigh}`,
      label: 'Your blood sugar goal (fasting)',
      sub: `Keep fasting readings in this range and after-meal readings below ${g.postMeal} mg/dL. A1c goal under ${g.a1c}%.`,
    };
  }
  if (plan.typeKey === 'weight') {
    return {
      big: `${g.targetWeight} lb`,
      label: 'Your weight goal',
      sub: `Weigh yourself as scheduled. Tell your care team about a gain of ${g.dayGain} lb in a day or ${g.weekGain} lb in a week.`,
    };
  }
  return {
    big: `${g.metric ? `${g.metric} ` : ''}${g.target || ''}`,
    label: 'Your goal',
    sub: g.description || '',
  };
}

export function getPrintMonitoring(plan) {
  const m = plan.monitoring || {};
  if (plan.typeKey === 'bp') {
    return {
      rows: [
        ['Device', m.device],
        ['How often', m.schedule],
        ['Days needed each month', m.requiredDaysPer30],
        ['We follow up if no reading for', m.missedReadingAlert],
      ],
      technique: BP_TECHNIQUE,
    };
  }
  if (plan.typeKey === 'glucose') {
    return {
      rows: [
        ['Device', m.device],
        ['When to test', m.schedule],
        ['Days needed each month', m.requiredDaysPer30],
        ['We follow up if no reading for', m.missedReadingAlert],
      ],
      technique: GLU_TECHNIQUE,
    };
  }
  if (plan.typeKey === 'weight') {
    return {
      rows: [
        ['Device', m.device],
        ['When to weigh', m.schedule],
        ['Days needed each month', m.requiredDaysPer30],
        ['We follow up if no reading for', m.missedReadingAlert],
      ],
      technique: WT_TECHNIQUE,
    };
  }
  return {
    rows: [
      ['Method', m.device],
      ['How often', m.schedule],
      ['Days needed each month', m.requiredDaysPer30],
      ['We follow up if no reading for', m.missedReadingAlert],
    ],
    technique: [],
  };
}

export function getPrintBands(plan) {
  const g = plan.goal || {};
  if (plan.typeKey === 'bp') {
    return [
      ['#1f9d55', `At or below your goal (${g.systolic}/${g.diastolic} mmHg)`, 'You’re on track. Keep taking your readings on schedule.'],
      ['#b45309', 'Up to 159 / 99', 'Your care team will reach out. Keep measuring and note how you feel.'],
      ['#c2560f', '160–179 / 100–119', 'Contact your care team the same day. Rest 5 minutes, then re-check.'],
      ['#d92d20', '180 or higher / 120 or higher', 'Re-check after 5 minutes. If still this high, contact your provider right away.'],
    ];
  }
  if (plan.typeKey === 'glucose') {
    return [
      ['#d92d20', 'Below 70 mg/dL (low)', 'Eat or drink 15 g of fast sugar, wait 15 minutes, re-check. Below 54 or unable to treat — call 911.'],
      ['#1f9d55', 'In your range', 'You’re on track. Keep to your meals, medicine, and activity.'],
      ['#b45309', 'Up to 180', 'Note what you ate and when you took your medicine. Your care team may reach out.'],
      ['#c2560f', '181–250', 'Contact your care team the same day. Drink water and check for a missed dose.'],
      ['#d92d20', 'Above 250', 'Contact your provider right away. Call 911 for vomiting, confusion, or trouble breathing.'],
    ];
  }
  if (plan.typeKey === 'weight') {
    return [
      ['#1f9d55', 'Stable weight', 'You’re on track. Keep weighing in each day.'],
      ['#b45309', `Up ${g.dayGain} lb in a day or ${g.weekGain} lb in a week`, 'Tell your care team. Watch salt and fluids.'],
      ['#d92d20', 'More than that, or new swelling / breathlessness', 'Contact your provider the same day. Call 911 for severe trouble breathing.'],
    ];
  }
  return null;
}

function flattenPlan(plan) {
  const out = [];
  ['goal', 'monitoring'].forEach((sec) => {
    const obj = plan[sec] || {};
    Object.keys(obj).forEach((k) => {
      let v = obj[k];
      if (k === 'reviewDate') v = formatPlanDate(v);
      out.push({
        key: `${sec}.${k}`,
        label: FIELD_LABELS[k] || k,
        value: v === '' || v == null ? '—' : String(v),
      });
    });
  });
  out.push({
    key: 'notes',
    label: 'Clinical notes',
    value: plan.notes && String(plan.notes).trim() ? String(plan.notes).trim() : '—',
  });
  out.push({
    key: 'approval',
    label: 'Approval',
    value: plan.signoff?.approvalStatus === 'approved'
      ? (plan.signoff.approvedBy || 'Approved')
      : 'Pending approval',
  });
  return out;
}

function diffList(oldArr, newArr, singular) {
  const o = (oldArr || []).map((x) => x.label);
  const n = (newArr || []).map((x) => x.label);
  const changes = [];
  n.forEach((l) => {
    if (!o.includes(l)) changes.push({ field: `Added ${singular}`, from: '—', to: l });
  });
  o.forEach((l) => {
    if (!n.includes(l)) changes.push({ field: `Removed ${singular}`, from: l, to: '—' });
  });
  return changes;
}

export function diffPlan(oldP, newP) {
  const of = flattenPlan(oldP || {});
  const nf = flattenPlan(newP || {});
  const map = {};
  const order = [];
  of.forEach((f) => {
    map[f.key] = { label: f.label, from: f.value };
    order.push(f.key);
  });
  nf.forEach((f) => {
    if (!map[f.key]) {
      map[f.key] = { label: f.label, from: '—' };
      order.push(f.key);
    }
    map[f.key].to = f.value;
  });
  const changes = [];
  order.forEach((k) => {
    const m = map[k];
    const to = m.to === undefined ? '—' : m.to;
    if (String(m.from) !== String(to)) {
      changes.push({ field: m.label, from: m.from, to });
    }
  });
  return changes.concat(
    diffList(oldP?.interventions, newP?.interventions, 'intervention'),
    diffList(oldP?.education, newP?.education, 'education topic')
  );
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildPrintHtml(plan, patient) {
  const type = getPlanType(plan.typeKey);
  const approved = plan.signoff?.approvalStatus === 'approved';
  const listItems = (arr) => (
    arr?.length
      ? `<ul>${arr.map((x) => `<li>${escapeHtml(x.label)}</li>`).join('')}</ul>`
      : '<p class="muted">None recorded.</p>'
  );
  const g = getPrintGoal(plan);
  const mon = getPrintMonitoring(plan);
  const monRows = mon.rows.map((r) => `<div>${escapeHtml(r[0])}: <b>${escapeHtml(String(r[1] ?? '—'))}</b></div>`).join('');
  const tech = mon.technique?.length
    ? `<ul>${mon.technique.map((x) => `<li>${escapeHtml(x)}</li>`).join('')}</ul>`
    : '';
  const bandsData = getPrintBands(plan);
  const bands = bandsData
    ? `<table><tbody>${bandsData.map((r) => `<tr><td class="band" style="border-left:4px solid ${r[0]}"><b>${escapeHtml(r[1])}</b></td><td>${escapeHtml(r[2])}</td></tr>`).join('')}</tbody></table>`
    : (plan.monitoring?.escalation
      ? `<p>${escapeHtml(plan.monitoring.escalation)}</p>`
      : '<p class="muted">See your care team’s instructions.</p>');
  const notes = plan.notes && String(plan.notes).trim()
    ? `<div class="sec"><h2>Notes</h2><p>${escapeHtml(plan.notes)}</p></div>`
    : '';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(plan.planTitle)} — ${escapeHtml(patient.name)}</title><style>
@page{size:letter;margin:0.7in}*{box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;color:#0f2942;margin:0;line-height:1.5;font-size:12.5px}
.brand{display:flex;align-items:flex-end;justify-content:space-between;border-bottom:3px solid #03045E;padding-bottom:10px}
.brand-left{display:flex;flex-direction:column;align-items:flex-start;gap:3px}
.brand-logo-img{height:44px;width:auto;max-width:170px;object-fit:contain;display:block}
.brand-sub{display:block;font-size:8px;letter-spacing:.14em;color:#64748b;font-weight:700;text-transform:uppercase}
.brand .logo{font-size:22px;font-weight:800;color:#03045E}.brand .logo small{display:block;font-size:8px;letter-spacing:.14em;color:#64748b;font-weight:700}
.brand .doc{text-align:right}.brand .doc h1{margin:0;font-size:17px;color:#1B2A4A}.brand .doc .st{font-size:11px;font-weight:700;padding:2px 9px;border-radius:99px;display:inline-block;margin-top:4px}
.st.ap{background:#ecfdf5;color:#15803d}.st.pd{background:#fff7ed;color:#c2410c}
.pt{display:grid;grid-template-columns:1fr 1fr;gap:2px 26px;margin:14px 0;font-size:12px;color:#475569}.pt b{color:#0f2942}
.signoff{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:9px 13px;font-size:11.5px;color:#475569;margin-bottom:6px}.signoff b{color:#0f2942}
.goalbox{display:flex;align-items:center;gap:16px;border:2px solid #03045E;border-radius:10px;padding:12px 18px;margin:14px 0}.goalbox .n{font-size:30px;font-weight:800;color:#03045E;white-space:nowrap}.goalbox .l{font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#64748b;font-weight:700}
.sec{margin-top:16px}.sec h2{font-size:13.5px;color:#1B2A4A;margin:0 0 7px;border-bottom:1px solid #e2e8f0;padding-bottom:4px}
.grid2{display:grid;grid-template-columns:1fr 1fr;gap:4px 26px;font-size:12.5px}ul{margin:4px 0 0;padding-left:18px}li{margin:2px 0}
table{width:100%;border-collapse:collapse;font-size:12px}td{padding:7px 9px;border-bottom:1px solid #eef1f5;vertical-align:top}.band{width:44%}
.emerg{border:2px solid #dc2626;background:#fef2f2;border-radius:10px;padding:11px 14px;margin-top:14px}.emerg h3{margin:0 0 4px;color:#dc2626;font-size:13px}.emerg p{margin:0;font-size:12px}
.muted{color:#94a3b8}.sign{display:grid;grid-template-columns:1fr 1fr;gap:30px;margin-top:26px}.sign .line{border-top:1px solid #0f2942;padding-top:5px;font-size:11px;color:#64748b}
.foot{margin-top:22px;border-top:1px solid #e2e8f0;padding-top:8px;font-size:10px;color:#94a3b8;text-align:center}
</style></head><body>
<div class="brand"><div class="brand-left"><img class="brand-logo-img" src="${EVITALS_LOGO_BASE64}" alt="e-Vitals" /><span class="brand-sub">REMOTE PATIENT MONITORING</span></div><div class="doc"><h1>${escapeHtml(plan.planTitle)}</h1><span class="st ${approved ? 'ap' : 'pd'}">${approved ? 'Approved' : 'Pending approval'}</span></div></div>
<div class="pt"><div>Patient: <b>${escapeHtml(patient.name)}</b></div><div>Patient ID: <b>${escapeHtml(patient.id)}</b></div><div>Date of birth: <b>${escapeHtml(patient.dob)}</b>${patient.age != null ? ` (Age ${escapeHtml(patient.age)})` : ''}</div><div>Sex: <b>${escapeHtml(patient.sex)}</b></div><div>Provider: <b>${escapeHtml(patient.provider)}</b></div><div>Caregiver: <b>${escapeHtml(patient.caregiver)}</b></div></div>
<div class="signoff">Created <b>${formatPlanDate(plan.signoff?.createdDate)}</b> by <b>${escapeHtml(plan.signoff?.createdBy)}</b> &nbsp;·&nbsp; Approved by <b>${escapeHtml(plan.signoff?.approvedBy || 'Pending')}</b> &nbsp;·&nbsp; Next review <b>${formatPlanDate(plan.goal?.reviewDate)}</b></div>
<div class="goalbox"><div class="n">${escapeHtml(g.big || '—')}</div><div><div class="l">${escapeHtml(g.label)}</div><div>${escapeHtml(g.sub || '')}</div></div></div>
<div class="sec"><h2>Taking your readings</h2><div class="grid2">${monRows}</div>${tech}</div>
<div class="sec"><h2>What to do about your numbers</h2>${bands}</div>
<div class="emerg"><h3>Call 911 right away</h3><p>${escapeHtml(type.emergency)}</p></div>
<div class="sec"><h2>Your care plan includes</h2>${listItems(plan.interventions)}</div>
<div class="sec"><h2>What we’ll go over together</h2>${listItems(plan.education)}</div>
${notes}
<div class="sign"><div class="line">Patient signature &nbsp;·&nbsp; Date</div><div class="line">Provider signature &nbsp;·&nbsp; Date</div></div>
<div class="foot">Confidential patient health information · e-Vitals Remote Patient Monitoring · Generated ${formatPlanDate(new Date().toISOString())}</div>
</body></html>`;
}

export function printCarePlan(plan, patient) {
  const w = window.open('', '_blank', 'width=840,height=1000');
  if (!w) {
    return false;
  }
  w.document.open();
  w.document.write(buildPrintHtml(plan, patient));
  w.document.close();
  w.focus();
  w.onload = () => {
    setTimeout(() => w.print(), 150);
  };
  return true;
}

export function buildPlanEmailText(plan, patient) {
  const g = getPrintGoal(plan);
  const mon = getPrintMonitoring(plan);
  const lines = [
    plan.planTitle,
    `Patient: ${patient.name} (ID ${patient.id})`,
    `Provider: ${patient.provider}`,
    `Created ${formatPlanDate(plan.signoff?.createdDate)} by ${plan.signoff?.createdBy || '—'} | Approved by ${plan.signoff?.approvedBy || 'Pending'}`,
    `Status: ${plan.signoff?.approvalStatus === 'approved' ? 'Approved' : 'Pending approval'}`,
    '',
    `GOAL: ${(g.big ? `${g.big}  ` : '')}${g.label}`,
  ];
  if (g.sub) lines.push(`  ${g.sub}`);
  lines.push('', 'MONITORING:');
  mon.rows.forEach((r) => lines.push(`  - ${r[0]}: ${r[1]}`));
  if (plan.interventions?.length) {
    lines.push('', 'CARE PLAN INCLUDES:');
    plan.interventions.forEach((x) => lines.push(`  - ${x.label}`));
  }
  if (plan.education?.length) {
    lines.push('', 'EDUCATION:');
    plan.education.forEach((x) => lines.push(`  - ${x.label}`));
  }
  if (plan.notes && String(plan.notes).trim()) {
    lines.push('', `NOTES: ${plan.notes}`);
  }
  lines.push('', `Next review: ${formatPlanDate(plan.goal?.reviewDate)}`);
  lines.push('', 'Confidential patient health information — e-Vitals Remote Patient Monitoring');
  return lines.join('\n');
}

export function logUpdateCount(plan) {
  return (plan.history || []).filter((h) => h.action === 'updated').length;
}
